const MOBILE_PROD_API_ORIGIN = "https://craft2.manucraft3d.com";
const MOBILE_STAGING_API_ORIGIN = "https://staging-craft2.manucraft3d.com";
// These aliases share API routing. app.connectgroove.com deliberately uses the configured API origin.
const WEB_PRODUCTION_HOST_ALIASES = new Set(["craft2.manucraft3d.com", "leads.trevi.ca"]);

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, "");

/** Strips trailing /api or /api/ then trims slashes so API_BASE_URL does not duplicate "/api". */
const normalizeOrigin = (value: string): string => trimTrailingSlash(value.replace(/\/api\/?$/i, ""));
const hasScheme = (value: string): boolean => /^https?:\/\//i.test(value);

const isWebProductionAlias = (origin: string): boolean => {
    try {
        const { hostname } = new URL(origin);
        return WEB_PRODUCTION_HOST_ALIASES.has(hostname);
    } catch {
        return false;
    }
};

const currentWebProductionAliasOrigin = (): string => {
    if (typeof window === "undefined") return "";
    if (window.location.protocol !== "https:") return "";
    if (!WEB_PRODUCTION_HOST_ALIASES.has(window.location.hostname)) return "";
    return window.location.origin;
};

export const toSafeOrigin = (value: string): string => {
    const raw = value.trim();
    if (!raw) return "";

    const candidate = hasScheme(raw)
        ? raw
        : (typeof window !== "undefined" ? `${window.location.protocol}//${raw}` : `https://${raw}`);

    try {
        return normalizeOrigin(new URL(candidate).origin + new URL(candidate).pathname);
    } catch {
        return "";
    }
};

export const isNativePlatform = (): boolean => {
    if (typeof window === "undefined") return false;
    const isCapacitorHost = window.location.hostname === "cg.app";

    const capacitor = (window as Window & {
        Capacitor?: {
            isNativePlatform?: () => boolean;
            getPlatform?: () => string;
        };
    }).Capacitor;

    if (!capacitor) {
        // Capacitor bridge may not be attached yet during early bootstrap.
        return isCapacitorHost;
    }
    if (typeof capacitor.isNativePlatform === "function") {
        if (capacitor.isNativePlatform()) {
            return true;
        }
    }
    if (typeof capacitor.getPlatform === "function") {
        if (capacitor.getPlatform() !== "web") {
            return true;
        }
    }

    if (isCapacitorHost) {
        return true;
    }

    return false;
};

export const getApiOrigin = (): string => {
    const envOrigin = import.meta.env.VITE_API_BASE_URL?.trim();
    if (envOrigin) {
        const safe = toSafeOrigin(envOrigin);
        const currentAliasOrigin = currentWebProductionAliasOrigin();
        if (safe && currentAliasOrigin && isWebProductionAlias(safe)) return currentAliasOrigin;
        if (safe) return safe;
    }

    const deployEnv = (import.meta.env.VITE_DEPLOY_ENV || import.meta.env.MODE || "").toLowerCase();

    if (isNativePlatform()) {
        const nativeOverride = import.meta.env.VITE_API_BASE_URL_NATIVE?.trim();
        if (nativeOverride) {
            const safe = toSafeOrigin(nativeOverride);
            if (safe) return safe;
        }

        if (deployEnv === "test" || deployEnv === "staging" || deployEnv === "qa") {
            const stagingOverride = import.meta.env.VITE_API_BASE_URL_STAGING?.trim();
            if (stagingOverride) {
                const safe = toSafeOrigin(stagingOverride);
                if (safe) return safe;
            }
            return MOBILE_STAGING_API_ORIGIN;
        }

        const prodOverride = import.meta.env.VITE_API_BASE_URL_PROD?.trim();
        if (prodOverride) {
            const safe = toSafeOrigin(prodOverride);
            if (safe) return safe;
        }

        return MOBILE_PROD_API_ORIGIN;
    }

    if (typeof window !== "undefined" && window.location.hostname === "app.connectgroove.com") {
        return MOBILE_PROD_API_ORIGIN;
    }

    return "";
};

/**
 * Public origin for the Craft 3D web app (e.g. https://craft2.manucraft3d.com).
 * - Optional `VITE_CRAFT_PUBLIC_ORIGIN` wins when set.
 * - On Capacitor, falls back to the API origin or the default prod craft host so
 *   the Capacitor virtual host (e.g. `cg.app`) does not become `craft2.app`.
 */
export const getCraftPublicOrigin = (): string => {
    const explicit = import.meta.env.VITE_CRAFT_PUBLIC_ORIGIN?.trim();
    if (explicit) {
        const safe = toSafeOrigin(explicit);
        if (safe) return trimTrailingSlash(safe);
    }

    if (typeof window !== "undefined" && window.location.hostname === "app.connectgroove.com") {
        return MOBILE_PROD_API_ORIGIN;
    }

    if (isNativePlatform()) {
        const apiOrigin = getApiOrigin();
        if (apiOrigin) return trimTrailingSlash(apiOrigin);
        return MOBILE_PROD_API_ORIGIN;
    }

    return "";
};

export const API_BASE_URL = `${getApiOrigin()}/api`;

/** Resolve legacy root-relative API paths without changing request options or auth. */
export const buildApiUrl = (path: string): string => {
    if (!path.startsWith("/api/")) return path;
    return `${getApiOrigin()}${path}`;
};
